import React from 'react'
import {observer} from 'mobx-react-lite'
import {Link} from '../../../link'
import {
  useBasketStore,
  useCustomerStore,
  useOrderStore,
  useRapidDeliveryStore
} from '../../../../store/hooks/useStore'
import {Box, Flex, Stack, Text, BaseButton} from '../../../../vanilla'
import useDeliveryBooking from '../../../../hooks/use-delivery-booking'
import LoadingOverlay from '../../../loading-overlay'
import {ExclamationCircleIcon} from '../../../icons'
import {DeliveryOrderInfo} from '../../../book-delivery/delivery-order-info'
import {useSlotExpiration} from '../../../../store/hooks/useSlotExpiration'
import {useSlotReservation} from '../../../../store/hooks/useSlotReservation'
import SlotsErrorMessaging from '../../../../pages/book-delivery/partials/booking-slots/error-messaging'
import {DeliveryTimeAndDate} from '../../../book-delivery/delivery-time-and-date'
import { RapidDeliveryDateTime } from '../../../book-delivery/rapid-delivery-date-time'

const DeliverySectionContainer = () => {
  const {
    deliveryPostalCode,
    postcodeError,
    setPostcodeError
  } = useBasketStore()

  if (postcodeError) {
    return (
      <Stack
        paddingX="8px"
        paddingY="12px"
        bg="white"
        borderBottom="1px"
        borderColor="gray200"
        alignItems="center"
      >
        <SlotsErrorMessaging
          variant="miniCart"
          buttonAction={() => {
            setPostcodeError?.(false)
          }}
        />
      </Stack>
    )
  }

  return (
    <Stack paddingX="8px" paddingY="12px" bg="white" borderBottom="1px" borderColor="gray200">
      {!deliveryPostalCode ? (
        <Text variant="text4" lineHeight="short" color="gray800">
          Check if we can deliver to you
        </Text>
      ) : (
        <EnteredPostCodeStates postCode={deliveryPostalCode} />
      )}
    </Stack>
  )
}

export const DeliverySection = observer(DeliverySectionContainer)

const EnteredPostCodeStates = observer(({postCode}: {postCode: string}) => {
  const {currentDeliverySlotId, isAppliedPostCodeDeliverable} = useBasketStore()

  return currentDeliverySlotId ? (
    <BookedDelivery />
  ) : (
    <CartDrawerChangeOrBookDelivery
      postcode={postCode}
      isDeliverable={isAppliedPostCodeDeliverable || false}
    />
  )
})

interface CartDrawerChangeOrBookDeliveryProps {
  postcode: string
  isDeliverable: boolean
}

const CartDrawerChangeOrBookDelivery = ({
  postcode = '',
  isDeliverable = false
}: CartDrawerChangeOrBookDeliveryProps) => {
  const {loading} = useDeliveryBooking()
  const {recentOrder} = useOrderStore()

  const {nearestStoreByDeliveryPostCode: store} = useBasketStore()
  const {isRegistered} = useCustomerStore()

  return (
    <Stack
      flexDirection="row"
      spacing="24px"
      position="relative"
      data-test-selector="book-delivery"
      justify={['space-between', 'flex-start']}
    >
      <LoadingOverlay isLoading={loading} />
      {recentOrder ? (
        <Flex direction="column" gap="12px">
          <DeliveryOrderInfo order={recentOrder} isMiniBasket={true} />
        </Flex>
      ) : isDeliverable ? (
        <>
          <Stack spacing="4px" justifyContent="center" >
            <Text
              variant="text4"
              color="gray800"
              lineHeight="none"
              data-cs-mask=""
            >{`Great! We'll deliver to ${postcode}`}</Text>
          </Stack>
          <BaseButton
            as={Link}
            data-test-selector="book-delivery-button"
            href="/select-delivery"
            variant="secondary"
            size="sm"
            alignSelf="flex-end"
            marginLeft="auto"
          >
            <Text variant="unstyled" fontSize="inherit" lineHeight="inherit" fontWeight="inherit">
              Book Delivery
            </Text>
          </BaseButton>
        </>
      ) : (
        <Stack width="full">
          <Box>
            <Text variant="text4" lineHeight="none" color="accent0" data-cs-mask="">
              Sorry, we&apos;re unable to deliver to {postcode}
            </Text>
          </Box>

          {store && (
            <Box>
              <Text variant="text4" as="span">
                Your nearest store is {store.name}.
              </Text>
              {!isRegistered && (
                <BaseButton
                  as={Link}
                  href="/account/register"
                  variant="secondary"
                  style={{minWidth: '100%'}}
                  width={['full', 'auto']}
                  maxHeight="36px"
                >
                  <Text variant="unstyled" fontSize="inherit" fontWeight="inherit">
                    Register
                  </Text>
                </BaseButton>
              )}
            </Box>
          )}

          <BaseButton
            as={Link}
            data-test-selector="change-address-button"
            href="/select-delivery"
            variant="secondary"
            size="sm"
            alignSelf="flex-end"
            marginLeft="auto"
          >
            <Text variant="unstyled" fontSize="inherit" lineHeight="inherit" fontWeight="inherit">
              Change Address
            </Text>
          </BaseButton>
        </Stack>
      )}
    </Stack>
  )
}

const BookedDeliveryContainer = () => {
  const basketStore = useBasketStore()
  const {isAvailable} = basketStore.currentDeliveryDay || {}
  const {isReservationExpired} = useSlotExpiration()
  const {handleSlotRebook} = useSlotReservation()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const bookingDeliveryUrl = '/select-delivery'

  // Checks if user is logged in before rebooking slot
  const handleSlotReservation = async () => {
    await handleSlotRebook(basketStore.currentDeliverySlotId, false)
  }

  const buttonText = () => {
    if (isRapidDeliveryBooked) {
      return 'Book Now'
    }
    return isAvailable ? 'Rebook' : 'Change Delivery'
  }

  if (isRapidDeliveryBooked) {
    return (
        <RapidDeliveryDateTime isMiniBasket={true} />
    )
  }

  const timeAndDate = () => {
    return (
      <DeliveryTimeAndDate
        startHour={basketStore.slotTime(basketStore?.basket?.c_windowStartTime)}
        endHour={basketStore.slotTime(basketStore?.basket?.c_windowEndTime)}
        textProps={{color: 'gray800', variant: 'text3', lineHeight: 'short'}}
        showDate={true}
        windowStartTime={basketStore?.basket?.c_windowStartTime}
        windowEndTime={basketStore?.basket?.c_windowEndTime}
      />
    )
  }

  const renderContent = () => {
    if (isReservationExpired()) {
      return (
        <Flex gap="8px" width="full" justify="space-between" align="center" flexDirection="column">
          <Flex gap="4px" align="center" justifyContent="flex-start" width="full">
            <ExclamationCircleIcon color="accent0" boxSize="16px" flexShrink="0" />
            <Text
              color="accent0"
              variant="text3"
              data-test-selector="delivery-reservation-expired"
              >
              Your delivery reservation has expired!
            </Text>
          </Flex>
          <Flex gap="4px" align="center" width="full" justifyContent="space-between">
            <Flex flexDirection="column">
            {timeAndDate()}
            </Flex>
            <BaseButton
              variant="outlinePrimary"
              size="sm"
              data-test-selector="book-delivery-time-button"
              alignSelf="center"
              flexShrink="0"
              as={Link}
              to={bookingDeliveryUrl}
              {...(isAvailable
              ? {onClick: () => handleSlotReservation()}
              : {as: Link, to: bookingDeliveryUrl})}
            >
              <Text variant="unstyled" fontSize="inherit" lineHeight="inherit" fontWeight="inherit">
                {buttonText()}
              </Text>
            </BaseButton>
          </Flex>
        </Flex>
      )
    }

    return (
      <Flex gap="16px" width="full" justify="space-between">
        <Stack spacing="2px" flexShrink="0">
          <Text color="gray800" lineHeight="short" variant="text4">
            Your delivery time:
          </Text>
          <DeliveryTimeAndDate
            textProps={{color: 'gray800', variant: 'text4', lineHeight: 'short'}}
          />
          {basketStore?.basket?.c_windowStartTime && (
            <Text color="gray800" lineHeight="short" variant="text4">
              {new Date(basketStore.basket.c_windowStartTime).toLocaleDateString('en-GB', {
                weekday: 'long',
                month: 'short',
                day: 'numeric'
              })}
            </Text>
          )}
        </Stack>
        <BaseButton
          variant="secondary"
          size="sm"
          data-test-selector="change-delivery-link"
          alignSelf="flex-end"
          flexBasis={['half', 'auto']}
          as={Link}
          to={bookingDeliveryUrl}
          style={{paddingTop: 6, paddingBottom: 6, whiteSpace: 'initial', height: 'auto', minHeight: 36}}
        >
          <Text variant="unstyled" fontSize="inherit" lineHeight="inherit" fontWeight="inherit" align="center">
            Change Delivery
          </Text>
        </BaseButton>
      </Flex>
    )
  }

  return (
    <Stack
      spacing="16px"
      flexDirection={['row', 'column']}
      flexWrap={['wrap', 'nowrap']}
      justifyContent={['space-between', 'flex-start']}
    >
      {renderContent()}
    </Stack>
  )
}

export const BookedDelivery = observer(BookedDeliveryContainer)
